import { useContext, useEffect, useState } from 'react';
import { useSessionsContext } from 'contexts/SessionsContext';
import SessionsList from 'containers/pages/sessions/SessionsList';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { ROUTE_SESSIONS_ENTITY_SESSION } from 'routes/RouteList';
import { TestSetV2Type } from 'constants';
import { sortAndGroupSessions } from 'utils/sort';
import { getTestSessionURL } from 'utils/url';
import { StoreContext } from 'index';
import Loader from 'components/loader/Loader';
import Button from 'components/button/Button';
import { FormattedMessage, useIntl } from 'react-intl';
import messages from 'messages';
import Icon from 'components/icon/Icon';
import Modal from 'components/modal/Modal';
import ExportSessionsCard from 'containers/partials/cards/ExportSessionsCard';
import { useAbility } from '@casl/react';
import { AbilityContext } from 'Can';
import { useContextMenu } from 'contexts/ContextMenuProvider';
import { ButtonExpandable } from 'components/button';
import { MenuWrapper } from 'components/menu';
import Session from 'models/Session';
import AddSessionModal from 'containers/pages/sessions/AddSessionModal';
import CopyUrlModal from 'containers/partials/modals/CopyUrlModal';

const OverviewSessionsTab = () => {
  const {
    uiState: { locale }
  } = useContext(StoreContext);
  const intl = useIntl();
  const { entityId } = useParams();
  const { push } = useHistory();
  const ability = useAbility(AbilityContext);
  const { sessions, loading, error } = useSessionsContext();
  const [urlModalContent, setUrlModalContent] = useState(null);
  const [copyUrlModal, setCopyUrlModal] = useState(false);
  const [exportModal, setExportModal] = useState(false);
  const [sessionModal, setSessionModal] = useState(null);

  const { setMenuItems, addActionButton, clearAllContext } = useContextMenu();

  useEffect(() => {
    if (ability.can('create', 'Sessions')) {
      if (ability.can('create', 'Prevention')) {
        addActionButton(
          <MenuWrapper
            extraClassNames={'c-menu__wrapper--expandable'}
            trigger={
              // TODO: maarten, ik zou hier eigenlijk geen dropdown zetten.
              // Gewoon een knop die een modal triggert, en in de modal kan
              // gekozen worden tussen prevention of talent (of rehab, evt)
              // <ButtonExpandable primary rounded>
              //   <Icon id="add-white" />
              //   <FormattedMessage {...messages.buttonSessionsAddSession} />
              //   <Icon id="dropdown-open" fillColor="color-neutral-x-light" />
              // </ButtonExpandable>
              <ButtonExpandable
                tabIndex={0}
                label={intl.formatMessage(messages.buttonSessionsAddSession)}
              >
                <Icon id="dropdown-open" fillColor="color-neutral-x-light" />
              </ButtonExpandable>
            }
          >
            <AddCustomSessionBtn icon="test" />
            <AddPreventionSessionBtn icon="test" />
          </MenuWrapper>
        );
      } else {
        addActionButton(
          <ButtonExpandable
            tabIndex={0}
            onClick={onClickNewTalentSessionHandler}
            label={intl.formatMessage(messages.buttonSessionsAddSession)}
          />
        );
      }
    }

    const contextButtons = [];
    // TODO: deze buttons hier activeren.
    // En bij de AddActionButton gewoon een button die een
    // modal triggert: in de modal kan de gebruiker kiezen
    // of over een custom of prevention session is
    // if (ability.can('create', 'Sessions')) {
    //   contextButtons.push(<AddCustomSessionBtn />);
    //   contextButtons.push(<AddPreventionSessionBtn />);
    // }
    if (ability.can('export', 'Sessions')) {
      contextButtons.push(<ExportSessionBtn />);
    }
    setMenuItems(contextButtons);

    return () => {
      clearAllContext();
    };
  }, [sessions]);

  const AddCustomSessionBtn = ({ ...props }) => (
    <Button menu onClick={onClickNewTalentSessionHandler}>
      <FormattedMessage {...messages.preventionAddTalentButton} />
      <Icon id={props.icon ? props.icon : 'add'} />
    </Button>
  );
  const AddPreventionSessionBtn = ({ ...props }) => (
    <Button menu onClick={onClickNewPreventionSessionHandler}>
      <FormattedMessage {...messages.preventionAddPreventionButton} />
      <Icon id={props.icon ? props.icon : 'add'} />
    </Button>
  );
  const ExportSessionBtn = () => (
    <Button
      menu
      onClick={() => setExportModal(true)}
      disabled={!sessions || sessions.length === 0}
    >
      <FormattedMessage {...messages.menuItemSessionExportSessions} />
      <Icon id="download" />
    </Button>
  );

  // Feature is not used for now, reason: it's not possible to select sessions
  const CopySessionUrlsBtn = () => (
    <Button
      menu
      onClick={() => setCopyUrlModal(true)}
      disabled={!urlModalContent}
    >
      {urlModalContent && urlModalContent.search('\n') !== -1 ? (
        <FormattedMessage {...messages.menuItemSessionCopyUrls} />
      ) : (
        <FormattedMessage {...messages.menuItemSessionOverviewCopyUrl} />
      )}
      <Icon id="copy" />
    </Button>
  );

  const onClickNewTalentSessionHandler = () => {
    setSessionModal({
      title: intl.formatMessage(messages.titleSessionsNewSession),
      type: 'session',
      session: new Session({
        type: TestSetV2Type.TALENT,
        recurring: false
      })
    });
  };

  const onClickNewPreventionSessionHandler = () => {
    setSessionModal({
      title: intl.formatMessage(messages.preventionModalAddNewTitle),
      type: 'prevention',
      session: new Session({
        type: TestSetV2Type.PREVENTION,
        recurring: false
      })
    });
  };

  const onSessionClickHandler = (session, tab) => {
    push(
      generatePath(ROUTE_SESSIONS_ENTITY_SESSION, {
        entityId,
        sessionsTab: tab,
        sessionId: session.id
      })
    );
  };

  const onSelectSessionsChangeHandler = selectedSessions => {
    setUrlModalContent(getModalContent(selectedSessions));
  };

  const getModalContent = selectedSessions => {
    if (!sessions) return null;

    const sessionsFiltered = selectedSessions.filter(s => !s.isDone);
    if (sessionsFiltered.length > 1) {
      const sortedSessions = sortAndGroupSessions(sessionsFiltered);
      const urls = sortedSessions.map(
        session =>
          session.name +
          ':\n' +
          getTestSessionURL({
            entityId,
            sessionId: session.id,
            packageType: session?.copy?.type ?? session.type,
            locale,
            personId: false,
            session,
            copyAble: true
          })
      );
      return urls.join('\n\n');
    } else if (sessionsFiltered.length === 1) {
      const session = sessionsFiltered[0];
      return getTestSessionURL({
        entityId,
        sessionId: session.id,
        packageType: session?.copy?.type ?? session?.type ?? 'talent',
        locale
      });
    }
    return null;
  };

  const onCloseModal = () => {
    setExportModal(false);
  };

  if (!sessions || sessions.length === 0) {
    if (loading) {
      return <Loader />;
    }
    if (error) {
      return <div>Error loading sessions: {error.message}</div>;
    }
  }

  return (
    <>
      <SessionsList
        onClick={session => onSessionClickHandler(session, 'sessions')}
        onSelected={onSelectSessionsChangeHandler}
      />
      {exportModal && (
        <Modal card autoHeight isOpen={true} onClose={onCloseModal}>
          <ExportSessionsCard onClose={onCloseModal} />
        </Modal>
      )}
      {copyUrlModal && (
        <CopyUrlModal
          isOpen={copyUrlModal}
          content={urlModalContent}
          onClose={() => {
            setCopyUrlModal(false);
            setUrlModalContent(null);
          }}
        />
      )}
      {sessionModal && (
        <AddSessionModal
          entityId={entityId}
          title={sessionModal.title}
          type={sessionModal.type}
          isCopy={sessionModal.isCopy}
          session={sessionModal.session}
          onClose={() => {
            setSessionModal(false);
          }}
        />
      )}
    </>
  );
};

export default OverviewSessionsTab;
